import React, { useEffect, useState } from 'react';
import Select from '@starbucks-web/pattern-library/lib/components/select';
import { useIntl } from 'react-intl';

import { usePdpContext } from '../../pdp-provider';
import messages from '../product-options-fields/messages';

const OtherOptions = ({
  category,
  categoryKeyPath,
  productOptions,
  topLevelCategoryName,
  topLevelCategoryNumber,
}) => {
  const { formatMessage, formatNumber, formats } = useIntl();
  const {
    isDemystifyMenuPdpEnabled,
    updateProductOption,
    productOptionDefaults,
    setMoveFocusToNewPicker,
    pricedProductFullOptions,
  } = usePdpContext();
  const [productOptionsList, setProductOptionsList] = useState([]);

  useEffect(() => {
    setProductOptionsList(productOptions);
  }, [productOptions]);

  // on change, based on the form code and clicked option,
  // create the appropriate option picker
  // TODO: Factor this function into a helper file with tests
  // /browse/WG-10925
  /* eslint-disable-next-line max-statements */
  const handleChange = (event) => {
    const clickedProductName = event.target.value;
    const clickedOption = productOptions.find(
      (option) => option.form.name === clickedProductName
    );
    const clickedProductNumber = clickedOption.productNumber;

    const formCode = clickedOption?.form?.formCode;
    const getSizeForSelectedOption = () => {
      if (formCode === 'modifier') {
        // "modifier" options have regular/no/light/extra as sizeCodes.
        // go ahead and set the 'regular' sizeCode as a selectedOption
        return clickedOption.form.sizes.find(
          (size) => size.sizeCode === 'regular'
        );
      }
      if (formCode === 'qty') {
        // "qty" options have sizeCode of 'add' or 'no'. We want 'add'.
        return clickedOption.form.sizes.find((size) => size.sizeCode === 'add');
      }
    };
    const getValueForSelectedOption = () => {
      if (formCode === 'qty') {
        // User chose a modifier like "Add vanilla syrup".
        // If there is a default provided, use it,
        // else select 1.
        const currentOptionDefault =
          productOptionDefaults[clickedProductNumber];
        return currentOptionDefault?.defaultValue ?? 1;
      }
    };
    // filter out the chosen option from list so it no longer
    // appears in the Select
    setProductOptionsList(
      productOptionsList.filter(
        (option) => option.form.name !== clickedProductName
      )
    );
    // Add this option to the array
    // of the customer's recipe choices.
    updateProductOption({
      categoryKeypath: categoryKeyPath,
      formCode,
      optionProductNumber: clickedProductNumber,
      size: getSizeForSelectedOption(),
      topLevelCategoryNumber,
      value: getValueForSelectedOption(),
    });
    // This event winds up creating a new component
    // representing this selection, and we'll have to
    // set focus to the new picker once it is rendered,
    // so we store a reference to it in provider state.
    setMoveFocusToNewPicker(clickedProductNumber);
  };

  const setOptionWithPrice = (optionsList) =>
    optionsList.map((option) => {
      const formName = option?.form?.name;
      const optionSku = option?.form?.sizes?.find((size) => {
        return pricedProductFullOptions?.get(size?.sku);
      })?.sku;

      const optionPrice = pricedProductFullOptions?.get(optionSku);
      const formattedOptionPrice = `, ${formatNumber(optionPrice, formats.number.money)}`;

      return (
        <option
          aria-label={`${formName} ${formattedOptionPrice}`}
          key={formName}
          value={formName}
        >
          {`${formName}${isDemystifyMenuPdpEnabled && optionPrice ? formattedOptionPrice : ''}`}
        </option>
      );
    });

  const id = `${topLevelCategoryName}-${category.name}-inactive-options`;

  return (
    <div className="pb2">
      <Select
        aria-label={formatMessage(messages.addProductOption, {
          productOptionName: category.name,
        })}
        data-e2e="other-options"
        id={id}
        key={id}
        label={formatMessage(messages.addProductOption, {
          productOptionName: category.name,
        })}
        onChange={handleChange}
        useInactiveStyle
        // This select should never show a chosen option.
        // Any time an option is chosen, it gets removed from the
        // options showing in the select.
        value={null}
      >
        {setOptionWithPrice(productOptionsList)}
      </Select>
    </div>
  );
};

export default OtherOptions;
